import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { User, LogOut, LayoutDashboard, Home, Info, ClipboardCheck, Video, Award, Menu, X } from 'lucide-react';
import logo from '../assets/logo.svg';

const Navigation = () => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const dashboardPath = user?.role === 'admin' ? '/admin' : user?.role === 'teacher' ? '/teacher' : '/dashboard';
  const close = () => setMenuOpen(false);

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/60 border-b border-white/20">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img src={logo} alt="Altanon Logo" className="w-10 h-10" />
            <span className="text-xl md:text-2xl font-outfit font-black text-slate-900 whitespace-nowrap">Altanon Learn</span>
          </Link>

          <div className="hidden xl:flex items-center gap-1.5">
            <Link to="/" data-testid="nav-home-link">
              <Button className="clay-button-secondary nav-btn" title="Home" aria-label="Home">
                <Home className="w-4 h-4 2xl:mr-1" /><span className="hidden 2xl:inline">Home</span>
              </Button>
            </Link>
            <Link to="/courses" data-testid="nav-courses-link">
              <Button className="clay-button-secondary nav-btn">Courses</Button>
            </Link>
            {user && (
              <Link to="/live" data-testid="nav-live-link">
                <Button className="clay-button-secondary nav-btn">
                  <Video className="w-4 h-4 mr-1" /> Live
                </Button>
              </Link>
            )}
            {user && (
              <Link to="/quizzes" data-testid="nav-quizzes-link">
                <Button className="clay-button-secondary nav-btn">
                  <ClipboardCheck className="w-4 h-4 mr-1" /> Quizzes
                </Button>
              </Link>
            )}
            {user?.role === 'student' && (
              <Link to="/certificates" data-testid="nav-certificates-link">
                <Button className="clay-button-secondary nav-btn">
                  <Award className="w-4 h-4 mr-1" /> Certificates
                </Button>
              </Link>
            )}
            <Link to="/about" data-testid="nav-about-link">
              <Button className="clay-button-secondary nav-btn" title="About Us" aria-label="About Us">
                <Info className="w-4 h-4 2xl:mr-1" /><span className="hidden 2xl:inline">About Us</span>
              </Button>
            </Link>

            {user ? (
              <>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-xl max-w-[11rem]" title={`${user.name} (${user.role})`}>
                  <User className="w-4 h-4 text-slate-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate">{user.name}</p>
                    <p className="text-xs capitalize text-slate-500 leading-tight">{user.role}</p>
                  </div>
                </div>
                <Link to={dashboardPath} data-testid="nav-dashboard-link">
                  <Button className="clay-button-secondary nav-btn">
                    <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
                  </Button>
                </Link>
                <Button onClick={handleLogout} className="clay-button-secondary nav-btn" data-testid="nav-logout-btn">
                  <LogOut className="w-4 h-4 mr-1" /> Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link">
                  <Button className="clay-button-secondary nav-btn">
                    <User className="w-4 h-4 mr-1" /> Login
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register-link">
                  <Button className="clay-button-primary nav-btn">Sign Up</Button>
                </Link>
              </>
            )}
          </div>

          <button
            className="xl:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            data-testid="nav-menu-btn"
          >
            {menuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {menuOpen && (
          <div className="xl:hidden mt-4 rounded-xl bg-white shadow-lg border border-slate-200 p-4 space-y-3" data-testid="nav-mobile-menu">
            <Link to="/" onClick={close} className="block">
              <Button className="w-full justify-start clay-button-secondary">
                <Home className="w-4 h-4 mr-2" /> Home
              </Button>
            </Link>
            <Link to="/courses" onClick={close} className="block">
              <Button className="w-full justify-start clay-button-secondary">Courses</Button>
            </Link>
            {user && (
              <Link to="/live" onClick={close} className="block">
                <Button className="w-full justify-start clay-button-secondary">
                  <Video className="w-4 h-4 mr-2" /> Live
                </Button>
              </Link>
            )}
            {user && (
              <Link to="/quizzes" onClick={close} className="block">
                <Button className="w-full justify-start clay-button-secondary">
                  <ClipboardCheck className="w-4 h-4 mr-2" /> Quizzes
                </Button>
              </Link>
            )}
            {user?.role === 'student' && (
              <Link to="/certificates" onClick={close} className="block">
                <Button className="w-full justify-start clay-button-secondary">
                  <Award className="w-4 h-4 mr-2" /> Certificates
                </Button>
              </Link>
            )}
            <Link to="/about" onClick={close} className="block">
              <Button className="w-full justify-start clay-button-secondary">
                <Info className="w-4 h-4 mr-2" /> About Us
              </Button>
            </Link>
            {user ? (
              <>
                <div className="rounded-lg bg-slate-100 p-3">
                  <p className="font-semibold">{user.name}</p>
                  <p className="text-sm capitalize text-slate-500">{user.role}</p>
                </div>
                <Link to={dashboardPath} onClick={close} className="block">
                  <Button className="w-full justify-start clay-button-secondary">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </Button>
                </Link>
                <Button onClick={handleLogout} className="w-full justify-start clay-button-secondary">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={close} className="block">
                  <Button className="w-full justify-start clay-button-secondary">
                    <User className="w-4 h-4 mr-2" /> Login
                  </Button>
                </Link>
                <Link to="/register" onClick={close} className="block">
                  <Button className="w-full clay-button-primary">Sign Up</Button>
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
